import PropTypes from 'prop-types';
import { useMemo } from 'react';

// material-ui
import { CssBaseline, StyledEngineProvider } from '@mui/material';
import { createTheme, ThemeProvider } from '@mui/material/styles';

// project import
import useConfig from 'hooks/useConfig';
import Palette from './palette';
import Typography from './typography';

import componentStyleOverrides from './compStyleOverride';
import customShadows from './shadows';

export default function ThemeCustomization({ children }) {
    const { borderRadius, fontFamily, navType, outlinedFilled, presetColor, rtlLayout } = useConfig();

    const theme = useMemo(() => Palette(navType, presetColor), [navType, presetColor]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const themeTypography = useMemo(() => Typography(theme, borderRadius, fontFamily), [theme, borderRadius, fontFamily]);
    const themeCustomShadows = useMemo(() => customShadows(navType, theme), [navType, theme]);

    const themeOptions = useMemo(
        () => ({
            direction: rtlLayout ? 'rtl' : 'ltr',
            palette: theme.palette,
            mixins: {
                toolbar: {
                    minHeight: '48px',
                    maxHeight: '80px',
                    padding: '16px',
                    '@media (min-width: 600px)': {
                        minHeight: '48px',
                        maxHeight: '80px'
                    }
                }
            },
            typography: themeTypography,
            customShadows: themeCustomShadows
        }),
        [rtlLayout, theme, themeCustomShadows, themeTypography]
    );

    const themes = useMemo(() => {
        const baseTheme = createTheme(themeOptions);
        const components = componentStyleOverrides(baseTheme, borderRadius, outlinedFilled);

        const finalTheme = createTheme({
            ...themeOptions,
            components
        });

        // Store custom typography properties separately to avoid frozen object issues in MUI v7
        // Create mutable copies of custom properties
        const customProps = [
            'mainContent',
            'menuCaption',
            'subMenuCaption',
            'commonAvatar',
            'smallAvatar',
            'mediumAvatar',
            'largeAvatar',
            'customInput'
        ];

        customProps.forEach((prop) => {
            if (themeTypography[prop]) {
                try {
                    Object.defineProperty(finalTheme.typography, prop, {
                        value: { ...themeTypography[prop] },
                        writable: true,
                        enumerable: true,
                        configurable: true
                    });
                } catch (e) {
                    // If defineProperty fails, theme.typography is sealed, skip
                    console.warn(`Cannot define ${prop} on typography`);
                }
            }
        });

        return finalTheme;
    }, [themeOptions, themeTypography, borderRadius, outlinedFilled]);

    return (
        <StyledEngineProvider injectFirst>
            <ThemeProvider theme={themes}>
                <CssBaseline />
                {children}
            </ThemeProvider>
        </StyledEngineProvider>
    );
}

ThemeCustomization.propTypes = {
    children: PropTypes.node
};
